/**
 * The OcrProvider seam (piece 2; decision 0347): a picture in, the words on
 * it with their boxes out, or the answer that nothing was read. The reading
 * is geometry the certificate reader interprets against a form template —
 * a printed label anchors the value beside or beneath it, and a reason box
 * is read as ticked by the dark share of the paper inside it. 🔨 The seam
 * never names a vendor: Google Cloud Vision is one adapter, tesseract the
 * offline one, and the fake here serves the tests and the sandbox.
 */
export interface OcrBox {
  /** Pixels from the picture's left edge. */
  x: number;
  /** Pixels from the picture's top edge. */
  y: number;
  width: number;
  height: number;
}

export interface OcrWord {
  text: string;
  box: OcrBox;
}

export interface OcrPage {
  width: number;
  height: number;
  words: readonly OcrWord[];
}

/** Why a picture was not read, where the seam knows (decision 0678): no Google Cloud Vision key is held. */
export type OcrUnreadReason = 'noVisionKey';

export interface OcrReading {
  status: 'read' | 'unread';
  pages: readonly OcrPage[];
  /** Why an unread answer is unread, where the seam can say; absent otherwise (decision 0678). */
  unreadReason?: OcrUnreadReason;
}

export interface OcrInput {
  bytes: Uint8Array;
  mimeType: string;
}

export interface OcrProvider {
  recognize(input: OcrInput): Promise<OcrReading>;
}

/** The picture's own pixels, asked for by the tick check; luminance runs 0 (black) to 255 (white). */
export interface PixelSampler {
  width: number;
  height: number;
  luminance(x: number, y: number): number;
}

export const UNREAD_OCR: OcrReading = { status: 'unread', pages: [] };

/** 🔒 A picture with no Vision key held reads as unread and says why by name (decision 0678). */
export const NO_VISION_KEY_OCR: OcrReading = { status: 'unread', pages: [], unreadReason: 'noVisionKey' };

/** A pixel darker than this counts as ink. */
export const INK_LUMINANCE = 128;
/**
 * The share of ink inside a box's inner half that reads as a tick. A printed
 * empty box is all border, and the inner half of it holds none; a tick or a
 * cross drawn through it darkens a fifth or more.
 */
export const TICK_DARK_SHARE = 0.12;
/** The words an OCR engine reads a tick or a cross as, when the picture's pixels are not to hand. */
export const TICK_MARKS: readonly string[] = ['x', '☒', '✓', '✔', '✗', '✘', '■', '●'];

export const OCR_MIME_TYPES: readonly string[] = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/tiff'];

/** The ink share inside a box, over the whole box; a box off the picture answers 0. */
export function darkShare(pixels: PixelSampler, box: OcrBox): number {
  const left = Math.max(0, Math.floor(box.x));
  const top = Math.max(0, Math.floor(box.y));
  const right = Math.min(pixels.width, Math.ceil(box.x + box.width));
  const bottom = Math.min(pixels.height, Math.ceil(box.y + box.height));
  if (right <= left || bottom <= top) return 0;
  let dark = 0;
  for (let y = top; y < bottom; y++) for (let x = left; x < right; x++) if (pixels.luminance(x, y) < INK_LUMINANCE) dark++;
  return dark / ((right - left) * (bottom - top));
}

/** The inner half of a box: each side pulled in by a quarter, so a printed border stays outside. */
export function innerHalf(box: OcrBox): OcrBox {
  return { x: box.x + box.width / 4, y: box.y + box.height / 4, width: box.width / 2, height: box.height / 2 };
}

/** The square a form prints its box in, immediately left of its label and as tall as the label's line. */
export function boxBesideLabel(label: OcrBox): OcrBox {
  const side = label.height * 1.2;
  return { x: label.x - side - label.height * 0.3, y: label.y + (label.height - side) / 2, width: side, height: side };
}

export interface OcrLine {
  y: number;
  height: number;
  words: readonly OcrWord[];
}

/** The page's words in reading order: lines top to bottom, words left to right. */
export function linesOf(page: OcrPage): OcrLine[] {
  const sorted = [...page.words].sort((a, b) => a.box.y + a.box.height / 2 - (b.box.y + b.box.height / 2) || a.box.x - b.box.x);
  const lines: { y: number; height: number; words: OcrWord[] }[] = [];
  for (const word of sorted) {
    const centre = word.box.y + word.box.height / 2;
    const line = lines.find((l) => Math.abs(l.y - centre) < Math.max(l.height, word.box.height) * 0.6);
    if (line) {
      line.words.push(word);
      line.words.sort((a, b) => a.box.x - b.box.x);
    } else lines.push({ y: centre, height: word.box.height, words: [word] });
  }
  return lines.sort((a, b) => a.y - b.y);
}

function normalized(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9%$&/'’-]/g, '');
}

export interface FoundLabel {
  line: OcrLine;
  /** Index of the label's first word on the line. */
  start: number;
  /** Index after the label's last word. */
  end: number;
  box: OcrBox;
}

/** Where a printed label stands: the first line carrying its words in order, punctuation and case set aside. */
export function findLabel(lines: readonly OcrLine[], label: string): FoundLabel | null {
  const tokens = label.split(/\s+/).map(normalized).filter(Boolean);
  if (tokens.length === 0) return null;
  for (const line of lines) {
    const words = line.words.map((w) => normalized(w.text));
    for (let start = 0; start + tokens.length <= words.length; start++) {
      if (tokens.every((token, i) => words[start + i] === token)) {
        const first = line.words[start]!.box;
        const last = line.words[start + tokens.length - 1]!.box;
        return { line, start, end: start + tokens.length, box: { x: first.x, y: Math.min(first.y, last.y), width: last.x + last.width - first.x, height: Math.max(first.height, last.height) } };
      }
    }
  }
  return null;
}

/**
 * The value a label anchors: the words after it on its own line, stopping
 * at the next label the template knows, or the words on the line beneath
 * that start under it.
 */
export function valueAtLabel(lines: readonly OcrLine[], found: FoundLabel, where: 'right' | 'below', otherLabels: readonly string[]): string {
  const stops = otherLabels.map((l) => l.split(/\s+/).map(normalized).filter(Boolean)).filter((t) => t.length > 0);
  const isStop = (words: readonly OcrWord[], at: number) => stops.some((tokens) => tokens.every((token, i) => normalized(words[at + i]?.text ?? '') === token));
  if (where === 'right') {
    const out: string[] = [];
    for (let i = found.end; i < found.line.words.length; i++) {
      if (isStop(found.line.words, i)) break;
      out.push(found.line.words[i]!.text);
    }
    return out.join(' ').trim();
  }
  const below = lines.find((line) => line.y > found.line.y + found.line.height * 0.5 && line.words.some((w) => w.box.x + w.box.width > found.box.x - found.box.height && w.box.x < found.box.x + Math.max(found.box.width, found.box.height * 12)));
  if (!below) return '';
  const out: string[] = [];
  const words = below.words.filter((w) => w.box.x + w.box.width > found.box.x - found.box.height);
  for (let i = 0; i < words.length; i++) {
    if (isStop(words, i)) break;
    out.push(words[i]!.text);
  }
  return out.join(' ').trim();
}

/**
 * Whether the box printed beside a label is ticked: by a mark the engine
 * read immediately left of the label, or by the ink inside the box's inner
 * half when the picture's pixels are to hand.
 */
export function tickedBesideLabel(found: FoundLabel, pixels: PixelSampler | null): boolean {
  const before = found.line.words[found.start - 1];
  if (before && TICK_MARKS.includes(before.text.trim().toLowerCase()) && before.box.x + before.box.width > found.box.x - found.box.height * 2.5) return true;
  return pixels !== null && darkShare(pixels, innerHalf(boxBesideLabel(found.box))) >= TICK_DARK_SHARE;
}

/** The page's words as one text, line by line, for the readers that work off text. */
export function textOfPage(page: OcrPage): string {
  return linesOf(page)
    .map((line) => line.words.map((w) => w.text).join(' '))
    .join('\n');
}

/** A grey picture held as bytes, one per pixel, row by row. */
export function grayscaleSampler(width: number, height: number, bytes: Uint8Array): PixelSampler {
  return { width, height, luminance: (x, y) => bytes[y * width + x] ?? 255 };
}

/**
 * The fake: a canned Pennsylvania REV-1220 read as words with boxes, the
 * blanket box marked and Resale ticked, so the certificate reader's
 * label-and-box path runs in a test. 🔨 A test double and nothing else: it
 * stood in for a missing key outside production until decision 0678, which
 * filled every sandbox picture with a form nobody uploaded.
 */
export const FAKE_OCR_PURCHASER = 'Fake OCR Purchaser';

export class FakeOcrProvider implements OcrProvider {
  constructor(private readonly reading: OcrReading = fakeCertificatePage()) {}

  async recognize(_input?: OcrInput): Promise<OcrReading> {
    return this.reading;
  }
}

/** Words laid on a 1000 by 1300 page, each row a line of the Pennsylvania form. */
export function fakeCertificatePage(): OcrReading {
  const rows: (string | { text: string; x?: number })[][] = [
    [{ text: 'REV-1220', x: 60 }, 'PENNSYLVANIA', 'EXEMPTION', 'CERTIFICATE'],
    [{ text: 'CHECK', x: 60 }, 'ONE:', { text: 'PENNSYLVANIA', x: 240 }, 'TAX', 'UNIT', 'EXEMPTION', 'CERTIFICATE'],
    [{ text: 'X', x: 200 }, { text: 'PENNSYLVANIA', x: 240 }, 'TAX', 'BLANKET', 'EXEMPTION', 'CERTIFICATE'],
    [{ text: 'Name', x: 60 }, 'of', 'Seller,', 'Vendor', 'or', 'Lessor', { text: 'Wicked', x: 400 }, 'Edge', 'Automation'],
    [{ text: 'X', x: 40 }, { text: '3.', x: 80 }, 'Property', 'will', 'be', 'resold', 'under', 'License', 'ID', { text: '12-345678', x: 620 }],
    [{ text: 'Name', x: 60 }, 'of', 'Purchaser', 'or', 'Lessee', { text: 'Fake', x: 400 }, 'OCR', 'Purchaser'],
    [{ text: 'Street', x: 60 }, { text: '220', x: 200 }, 'Industrial', 'Pkwy', { text: 'City', x: 500 }, 'Pittsburgh'],
    [{ text: 'EIN', x: 60 }, { text: '25-1234567', x: 200 }, { text: 'Date', x: 500 }, { text: '03/14/2026', x: 580 }],
  ];
  const words: OcrWord[] = [];
  rows.forEach((row, lineIndex) => {
    let x = 60;
    for (const cell of row) {
      const text = typeof cell === 'string' ? cell : cell.text;
      if (typeof cell !== 'string' && cell.x !== undefined) x = cell.x;
      const width = text.length * 11;
      words.push({ text, box: { x, y: 100 + lineIndex * 60, width, height: 20 } });
      x += width + 12;
    }
  });
  return { status: 'read', pages: [{ width: 1000, height: 1300, words }] };
}
